import jsPDF from 'jspdf';
import logoImage from '../assets/white-logo-transparent.png';

export const generatePaymentReceipt = (paymentDetails, playerDetails = null) => {
  const doc = new jsPDF();
  
  // Colors
  const primaryColor = [4, 12, 75]; // #040c4b
  const secondaryColor = [108, 117, 125]; // #6c757d
  const successColor = [40, 167, 69]; // #28a745
  const lightGray = [248, 249, 250]; // #f8f9fa
  
  // Helper function to add background rectangles
  const addBackground = (x, y, width, height, color = lightGray) => {
    doc.setFillColor(...color);
    doc.rect(x, y, width, height, 'F');
  };
  
  // Header with background
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 210, 50, 'F');
  
  // Add logo
  try {
    doc.addImage(logoImage, 'PNG', 15, 10, 25, 25);
  } catch (error) {
    console.warn('Could not load logo image:', error);
  }
  
  // Company title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Alhilal Club Academy Sports Company', 45, 25);
  
  // Receipt title
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('PAYMENT RECEIPT', 105, 60, { align: 'center' });
  
  // Success indicator with background
  addBackground(20, 70, 170, 12, [220, 253, 231]);
  doc.setTextColor(...successColor);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('✓ PAYMENT SUCCESSFUL', 105, 78, { align: 'center' });
  
  // Reset text color
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  
  let yPosition = 95;
  
  // Payment details section with compact layout
  addBackground(20, yPosition - 3, 170, 8);
  doc.setFont('helvetica', 'bold');
  doc.text('PAYMENT DETAILS', 25, yPosition + 2);
  yPosition += 15;
  
  doc.setFont('helvetica', 'normal');
  
  // Create a compact two-column layout
  const leftColumn = 25;
  const rightColumn = 110;
  const lineHeight = 10;
  
  // Transaction ID
  if (paymentDetails.paymentId) {
    doc.setFont('helvetica', 'bold');
    doc.text('Transaction ID:', leftColumn, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const transactionIdLines = doc.splitTextToSize(paymentDetails.paymentId, 75);
    doc.text(transactionIdLines, rightColumn, yPosition);
    doc.setFontSize(11);
    yPosition += lineHeight;
  }
  
  // Transaction Date
  if (paymentDetails.timestamp || playerDetails?.transactionDate) {
    const dateToUse = paymentDetails.timestamp || playerDetails.transactionDate;
    const formattedDate = new Date(dateToUse).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    doc.setFont('helvetica', 'bold');
    doc.text('Date:', leftColumn, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(formattedDate, rightColumn, yPosition);
    yPosition += lineHeight;
  }
  
  // Amount Paid
  if (paymentDetails.amount && paymentDetails.currency) {
    doc.setFont('helvetica', 'bold');
    doc.text('Amount Paid:', leftColumn, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...successColor);
    doc.setFontSize(12);
    doc.text(`${paymentDetails.currency} ${paymentDetails.amount}`, rightColumn, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    yPosition += lineHeight + 3;
  }
  
  // Points Used (if applicable - for Transfer type)
  if (playerDetails?.pointsUsed && playerDetails.pointsUsed > 0) {
    doc.setFont('helvetica', 'bold');
    doc.text('Points Used:', leftColumn, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...primaryColor);
    doc.text(`${playerDetails.pointsUsed.toFixed(2)} Points`, rightColumn, yPosition);
    doc.setTextColor(0, 0, 0);
    yPosition += lineHeight;
  }
  
  // Player details section if available
  if (playerDetails) {
    // Add some space
    yPosition += 5;
    
    addBackground(20, yPosition - 3, 170, 8);
    doc.setFont('helvetica', 'bold');
    doc.text('PLAYER INFORMATION', 25, yPosition + 2);
    yPosition += 15;
    
    doc.setFont('helvetica', 'normal');
    
    // Player Name - display as-is, even if it contains Arabic
    if (playerDetails.playerName) {
      doc.setFont('helvetica', 'bold');
      doc.text('Player Name:', leftColumn, yPosition);
      doc.setFont('helvetica', 'normal');
      // Try to display the name as-is
      try {
        doc.text(playerDetails.playerName || 'N/A', rightColumn, yPosition);
      } catch (e) {
        // If there's an error rendering, fallback to N/A
        doc.text('N/A', rightColumn, yPosition);
      }
      yPosition += lineHeight;
    }
    
    // Type - Show for Product payments
    if (playerDetails.paymentType === 'Product') {
      doc.setFont('helvetica', 'bold');
      doc.text('Type:', leftColumn, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text('Product Purchase', rightColumn, yPosition);
      yPosition += lineHeight;
    }
    
    // Membership - Hide for Product payments
    if (playerDetails.membershipName && playerDetails.paymentType !== 'Product') {
      doc.setFont('helvetica', 'bold');
      doc.text('Membership:', leftColumn, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(playerDetails.membershipName, rightColumn, yPosition);
      yPosition += lineHeight;
    }
    
    // Start Date - Hide for Product payments
    if (playerDetails.startDate && playerDetails.paymentType !== 'Product') {
      const startDate = new Date(playerDetails.startDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      doc.setFont('helvetica', 'bold');
      doc.text('Start Date:', leftColumn, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(startDate, rightColumn, yPosition);
      yPosition += lineHeight;
    }
    
    // Expiry Date - Hide for Product payments
    if (playerDetails.expiryDate && playerDetails.paymentType !== 'Product') {
      const expiryDate = new Date(playerDetails.expiryDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      doc.setFont('helvetica', 'bold');
      doc.text('Expiry Date:', leftColumn, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(expiryDate, rightColumn, yPosition);
      yPosition += lineHeight;
    }
    
    // Branch Name
    if (playerDetails.branchName) {
      doc.setFont('helvetica', 'bold');
      doc.text('Branch:', leftColumn, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(playerDetails.branchName, rightColumn, yPosition);
      yPosition += lineHeight;
    }
  }
  
  // Payment status section
  yPosition += 10;
  addBackground(20, yPosition - 3, 170, 12, [220, 253, 231]);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...successColor);
  doc.text('STATUS: PAID', 105, yPosition + 4, { align: 'center' });
  yPosition += 20;
  
  // Footer line
  doc.setTextColor(0, 0, 0);
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.5);
  doc.line(20, yPosition, 190, yPosition);
  
  yPosition += 10;
  
  // Footer text
  doc.setTextColor(...secondaryColor);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('© 2025 Al Hilal Academy. All rights reserved.', 105, yPosition, { align: 'center' });
  
  return doc;
};

export const downloadReceipt = (paymentDetails, playerDetails = null) => {
  try {
    const doc = generatePaymentReceipt(paymentDetails, playerDetails);
    const filename = `Al_Hilal_Academy_Receipt_${paymentDetails.paymentId || Date.now()}.pdf`;
    doc.save(filename);
    return true;
  } catch (error) {
    console.error('Error generating receipt:', error);
    return false;
  }
};

export const previewReceipt = (paymentDetails, playerDetails = null) => {
  try {
    const doc = generatePaymentReceipt(paymentDetails, playerDetails);
    const pdfBlob = doc.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
    return true;
  } catch (error) {
    console.error('Error previewing receipt:', error);
    return false;
  }
};

// Utility function to format payment data for receipt
export const formatPaymentDataForReceipt = (paymentData) => {
  console.log('Payment data for receipt formatting:', paymentData); // Debug log
  
  // Try multiple ways to get the transaction ID - prioritize 'id' field
  const transactionId = paymentData.id || 
                       paymentData.resultDetails?.connectorTxID1 || 
                       paymentData.paymentId ||
                       paymentData.transactionId ||
                       'N/A';
  
  console.log('Extracted transaction ID:', transactionId); // Debug log
  
  return {
    paymentId: transactionId,
    amount: paymentData.amount || '0.00',
    currency: paymentData.currency || 'SAR',
    paymentBrand: paymentData.paymentBrand || paymentData.payment_brand || 'Card',
    description: paymentData.description || 'Al Hilal Academy Membership Payment',
    customerInfo: null, // Hide customer information
    timestamp: paymentData.timestamp || new Date().toISOString(),
    productName: paymentData.productName || paymentData.product_name || 'Academy Membership'
  };
};
